/**
 * RenewalReminderBanner — shows in Dashboard 3 days before current_period_end
 * for paid subscriptions with status="active". Dismissible per session.
 * Also shows a distinct message when status="canceling" so the user knows the
 * plan is ending.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, XCircle, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "slidecon_renewal_reminder_dismissed";

export default function RenewalReminderBanner() {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    // dismissed only for the current calendar day
    try {
      const stored = sessionStorage.getItem(DISMISS_KEY);
      if (stored === new Date().toISOString().slice(0, 10)) setDismissed(true);
    } catch { /* ignore */ }
    fetch(`${API_URL}/api/payments/subscription`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setSub)
      .catch(() => setSub(null));
  }, [token, isAuthenticated]);

  if (dismissed || !sub) return null;
  const tier = sub.tier;
  const status = sub.status;
  const end = sub.current_period_end ? new Date(sub.current_period_end) : null;
  if (!tier || tier === "free" || !end) return null;

  const now = new Date();
  const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  const isCanceling = status === "canceling";
  // Show reminder only in the 3-day window (or any positive days-left for canceling)
  if (!isCanceling && (daysLeft < 0 || daysLeft > 3)) return null;
  if (isCanceling && daysLeft < 0) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, new Date().toISOString().slice(0, 10)); } catch { /* ignore */ }
  };

  const endStr = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

  return (
    <div
      data-testid="renewal-reminder-banner"
      className={`relative rounded-lg border p-3 pr-10 mb-4 flex items-start gap-3 ${
        isCanceling
          ? "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900"
          : "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-900"
      }`}
    >
      <div className={`shrink-0 mt-0.5 ${isCanceling ? "text-amber-600" : "text-sky-600"}`}>
        {isCanceling ? <XCircle className="w-5 h-5" /> : <CalendarClock className="w-5 h-5" />}
      </div>
      <div className="flex-1 text-sm">
        <p className={`font-semibold ${isCanceling ? "text-amber-900 dark:text-amber-100" : "text-sky-900 dark:text-sky-100"}`}>
          {isCanceling
            ? `Your ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan ends ${endStr}`
            : `Your ${tier.charAt(0).toUpperCase() + tier.slice(1)} plan renews ${endStr}`}
        </p>
        <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          {isCanceling
            ? "You'll keep every Pro feature until then. Change your mind? Restart anytime."
            : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in this billing cycle. Manage your plan or view invoices.`}
        </p>
        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => navigate("/pricing")}
            data-testid="renewal-manage-btn"
          >
            {isCanceling ? "Restart subscription" : "Manage plan"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => navigate("/invoices")}
            data-testid="renewal-invoices-btn"
          >
            View invoices
          </Button>
        </div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        data-testid="renewal-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
